import React, { useEffect, useState } from 'react';
import {
  Building2, Briefcase, MapPin, Mail, Phone, Hash, Users,
  Save, Globe, RotateCcw, Clock, ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AtLimit } from '../components/ui/char-counter';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showSuccess, showWarning } from '../components/Alerts';
import { handleApiError, handleLoadError } from '../utils/errorHandler';
import { formatCuit, isValidCuit } from '../utils/format';
import { companyProfileService } from '../services/companyProfileService';
import { CompanyProfile, CompanyProfileUpdateRequest } from '../types';
import { useAuth } from '../context/AuthContext';

// ── Constants ─────────────────────────────────────────────────────────────────

const RUBROS = [
  'Constructora / Obra civil',
  'Arquitectura y diseño',
  'Ingeniería civil',
  'Instalaciones (electricidad / plomería)',
  'Proveedores de materiales',
  'Desarrollo inmobiliario',
  'Administración de propiedades',
  'Otro',
];

const PROVINCIAS_AR = [
  'Buenos Aires',
  'Ciudad Autónoma de Buenos Aires',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Córdoba',
  'Corrientes',
  'Entre Ríos',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquén',
  'Río Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego, Antártida e Islas del Atlántico Sur',
  'Tucumán',
];

const EMPLEADOS_RANGOS = ['1–5', '6–15', '16–50', '51–200', '200+'];

// ── Helpers ───────────────────────────────────────────────────────────────────

const toFormData = (p: CompanyProfile): CompanyProfileUpdateRequest => ({
  companyName: p.companyName,
  legalName: p.legalName,
  taxId: p.taxId,
  industry: p.industry,
  companyEmail: p.companyEmail ?? '',
  companyPhone: p.companyPhone ?? '',
  address: p.address,
  country: p.country,
  province: p.province,
  city: p.city,
  employeeCount: p.employeeCount,
});

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

// ── Component ─────────────────────────────────────────────────────────────────

const CompanyProfileTab: React.FC = () => {
  const { isOwner, hasPermission } = useAuth();
  const canEdit = isOwner() || hasPermission('config.editar');

  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [formData, setFormData] = useState<CompanyProfileUpdateRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Validation states
  const [companyNameInvalid, setCompanyNameInvalid] = useState(false);
  const [legalNameInvalid, setLegalNameInvalid] = useState(false);
  const [taxIdInvalid, setTaxIdInvalid] = useState(false);
  const [addressInvalid, setAddressInvalid] = useState(false);
  const [cityInvalid, setCityInvalid] = useState(false);
  const [provinceInvalid, setProvinceInvalid] = useState(false);
  const [industryInvalid, setIndustryInvalid] = useState(false);
  const [employeeCountInvalid, setEmployeeCountInvalid] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const data = await companyProfileService.getProfile();
        setProfile(data);
        setFormData(toFormData(data));
      } catch (error) {
        handleLoadError(error);
      } finally {
        setLoading(false);
      }
    };
    void loadProfile();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <LoadingScreen fullScreen={false} message="Cargando perfil de empresa..." />
      </div>
    );
  }

  if (!profile || !formData) {
    return (
      <div className="p-6">
        <div className="p-4 border rounded-xl border-border bg-card text-sm text-muted-foreground">
          No se encontró información del perfil de empresa.
        </div>
      </div>
    );
  }

  const handleInputChange = (field: keyof CompanyProfileUpdateRequest, value: string) => {
    if (field === 'companyName' && value.trim()) setCompanyNameInvalid(false);
    if (field === 'legalName' && value.trim()) setLegalNameInvalid(false);
    if (field === 'taxId' && value.trim()) setTaxIdInvalid(false);
    if (field === 'address' && value.trim()) setAddressInvalid(false);
    if (field === 'city' && value.trim()) setCityInvalid(false);
    if (field === 'province' && value) setProvinceInvalid(false);
    if (field === 'industry' && value) setIndustryInvalid(false);
    if (field === 'employeeCount' && value) setEmployeeCountInvalid(false);

    setFormData(prev => prev ? { ...prev, [field]: value } : prev);
    setIsDirty(true);
  };

  const handleCancel = () => {
    setFormData(toFormData(profile));
    setIsDirty(false);
    setCompanyNameInvalid(false);
    setLegalNameInvalid(false);
    setTaxIdInvalid(false);
    setAddressInvalid(false);
    setCityInvalid(false);
    setProvinceInvalid(false);
    setIndustryInvalid(false);
    setEmployeeCountInvalid(false);
  };

  const handleSave = async () => {
    if (!formData) return;

    if (!formData.companyName.trim()) {
      setCompanyNameInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Nombre de la empresa');
      document.getElementById('companyName')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('companyName')?.focus();
      return;
    }
    if (!formData.legalName.trim()) {
      setLegalNameInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Razón social');
      document.getElementById('legalName')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('legalName')?.focus();
      return;
    }
    if (!formData.taxId.trim()) {
      setTaxIdInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'CUIT');
      document.getElementById('taxId')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('taxId')?.focus();
      return;
    }
    if (!isValidCuit(formData.taxId)) {
      setTaxIdInvalid(true);
      showWarning('El CUIT no es válido. Verificá los 11 dígitos y el dígito verificador.', 'CUIT');
      document.getElementById('taxId')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('taxId')?.focus();
      return;
    }
    if (!formData.industry) {
      setIndustryInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Rubro');
      return;
    }
    if (!formData.employeeCount) {
      setEmployeeCountInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Cantidad de empleados');
      return;
    }
    if (!formData.address.trim()) {
      setAddressInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Dirección');
      document.getElementById('address')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('address')?.focus();
      return;
    }
    if (!formData.province) {
      setProvinceInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Provincia');
      return;
    }
    if (!formData.city.trim()) {
      setCityInvalid(true);
      showWarning('Por favor, completa el campo obligatorio', 'Ciudad');
      document.getElementById('city')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('city')?.focus();
      return;
    }

    try {
      setSaving(true);
      const updated = await companyProfileService.updateProfile({
        ...formData,
        companyEmail: formData.companyEmail?.trim() || undefined,
        companyPhone: formData.companyPhone?.trim() || undefined,
      });
      setProfile(updated);
      setFormData(toFormData(updated));
      setIsDirty(false);
      showSuccess('Perfil de empresa actualizado correctamente');
    } catch (error) {
      handleApiError(error, 'Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  if (saving) return <LoadingScreen fullScreen={false} message="Guardando perfil de empresa..." />;

  return (
    <form
      onSubmit={e => { e.preventDefault(); handleSave(); }}
      className="px-4 py-6 space-y-8 sm:px-6"
    >

      {/* Última actualización */}
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-muted/30 w-fit text-xs text-muted-foreground">
        <Clock className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Última actualización: <span className="font-medium text-foreground">{formatDate(profile.updatedAt)}</span></span>
        <span className="mx-1 text-border">·</span>
        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
        <span>por <span className="font-medium text-foreground">{profile.updatedBy}</span></span>
      </div>

      {/* ── Sección 1: Identidad ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Building2 className="w-5 h-5 text-primary" />
            Identidad de la empresa
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Nombre comercial, razón social, CUIT y sector de actividad de tu organización.
          </p>
        </div>

        <div className="space-y-4 lg:col-span-2">

          {/* Nombre comercial */}
          <div className="space-y-2">
            <Label htmlFor="companyName" className="text-xs font-medium text-foreground sm:text-sm">
              Nombre de la empresa <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <Input
              id="companyName"
              value={formData.companyName}
              onChange={e => handleInputChange('companyName', e.target.value)}
              maxLength={200}
              disabled={!canEdit}
              className={`rounded-lg bg-background border-border focus-visible:ring-primary ${
                companyNameInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''
              }`}
              placeholder="Ej: Constructora Atlas S.A."
            />
            <AtLimit value={formData.companyName} max={200} />
          </div>

          {/* Razón social + CUIT */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="legalName" className="text-xs font-medium text-foreground sm:text-sm">
                Razón social <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="legalName"
                  value={formData.legalName}
                  onChange={e => handleInputChange('legalName', e.target.value)}
                  maxLength={200}
                  disabled={!canEdit}
                  className={`pl-9 rounded-lg bg-background border-border focus-visible:ring-primary ${
                    legalNameInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''
                  }`}
                  placeholder="Ej: Atlas Construcciones S.R.L."
                />
              </div>
              <AtLimit value={formData.legalName} max={200} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxId" className="text-xs font-medium text-foreground sm:text-sm">
                CUIT <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="taxId"
                  value={formData.taxId}
                  onChange={e => handleInputChange('taxId', formatCuit(e.target.value))}
                  maxLength={50}
                  disabled={!canEdit}
                  className={`pl-9 rounded-lg bg-background border-border focus-visible:ring-primary ${
                    taxIdInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''
                  }`}
                  placeholder="30-12345678-1"
                />
              </div>
              <AtLimit value={formData.taxId} max={50} />
            </div>
          </div>

          {/* Rubro + Empleados */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="industry" className="text-xs font-medium text-foreground sm:text-sm">
                Rubro <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
                <Select
                  value={formData.industry}
                  onValueChange={val => handleInputChange('industry', val)}
                  disabled={!canEdit}
                >
                  <SelectTrigger
                    id="industry"
                    className={`pl-9 rounded-lg bg-background border-border ${
                      industryInvalid ? 'border-red-500 border-2' : ''
                    }`}
                  >
                    <SelectValue placeholder="Seleccioná un rubro..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RUBROS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeCount" className="text-xs font-medium text-foreground sm:text-sm">
                Cantidad de empleados <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
                <Select
                  value={formData.employeeCount}
                  onValueChange={val => handleInputChange('employeeCount', val)}
                  disabled={!canEdit}
                >
                  <SelectTrigger
                    id="employeeCount"
                    className={`pl-9 rounded-lg bg-background border-border ${
                      employeeCountInvalid ? 'border-red-500 border-2' : ''
                    }`}
                  >
                    <SelectValue placeholder="Seleccioná un rango..." />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLEADOS_RANGOS.map(r => (
                      <SelectItem key={r} value={r}>{r} empleados</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border" />

      {/* ── Sección 2: Contacto ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Mail className="w-5 h-5 text-primary" />
            Información de contacto
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Email corporativo y teléfono de contacto de tu empresa. Ambos son opcionales.
          </p>
        </div>

        <div className="space-y-4 lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="companyEmail" className="text-xs font-medium text-foreground sm:text-sm">
                Email corporativo
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="companyEmail"
                  type="email"
                  value={formData.companyEmail ?? ''}
                  onChange={e => handleInputChange('companyEmail', e.target.value)}
                  maxLength={255}
                  disabled={!canEdit}
                  className="pl-9 rounded-lg bg-background border-border focus-visible:ring-primary"
                  placeholder="contacto@empresa.com"
                />
              </div>
              <AtLimit value={formData.companyEmail ?? ''} max={255} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyPhone" className="text-xs font-medium text-foreground sm:text-sm">
                Teléfono de contacto
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="companyPhone"
                  type="tel"
                  value={formData.companyPhone ?? ''}
                  onChange={e => handleInputChange('companyPhone', e.target.value)}
                  maxLength={80}
                  disabled={!canEdit}
                  className="pl-9 rounded-lg bg-background border-border focus-visible:ring-primary"
                  placeholder="+54 11 1234-5678"
                />
              </div>
              <AtLimit value={formData.companyPhone ?? ''} max={80} />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border" />

      {/* ── Sección 3: Ubicación ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <MapPin className="w-5 h-5 text-primary" />
            Ubicación
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            País, provincia, ciudad y dirección física de tu empresa.
          </p>
        </div>

        <div className="space-y-4 lg:col-span-2">
          {/* País (solo lectura) */}
          <div className="space-y-2">
            <Label htmlFor="country" className="text-xs font-medium text-foreground sm:text-sm">
              País
            </Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                id="country"
                value={formData.country}
                disabled
                className="pl-9 rounded-lg bg-muted/40 border-border cursor-not-allowed opacity-70"
              />
            </div>
            <p className="text-xs text-muted-foreground">El país no puede modificarse.</p>
          </div>

          {/* Provincia + Ciudad */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="province" className="text-xs font-medium text-foreground sm:text-sm">
                Provincia <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
                <Select
                  value={formData.province}
                  onValueChange={val => handleInputChange('province', val)}
                  disabled={!canEdit}
                >
                  <SelectTrigger
                    id="province"
                    className={`pl-9 rounded-lg bg-background border-border ${
                      provinceInvalid ? 'border-red-500 border-2' : ''
                    }`}
                  >
                    <SelectValue placeholder="Seleccioná una provincia..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVINCIAS_AR.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-xs font-medium text-foreground sm:text-sm">
                Ciudad <span className="text-xs text-red-500">(Campo obligatorio)</span>
              </Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="city"
                  value={formData.city}
                  onChange={e => handleInputChange('city', e.target.value)}
                  maxLength={120}
                  disabled={!canEdit}
                  className={`pl-9 rounded-lg bg-background border-border focus-visible:ring-primary ${
                    cityInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''
                  }`}
                  placeholder="Ej: Mar del Plata"
                />
              </div>
              <AtLimit value={formData.city} max={120} />
            </div>
          </div>

          {/* Dirección */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-xs font-medium text-foreground sm:text-sm">
              Dirección <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                id="address"
                value={formData.address}
                onChange={e => handleInputChange('address', e.target.value)}
                maxLength={255}
                disabled={!canEdit}
                className={`pl-9 rounded-lg bg-background border-border focus-visible:ring-primary ${
                  addressInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''
                }`}
                placeholder="Ej: Av. Corrientes 1234, Piso 3"
              />
            </div>
            <AtLimit value={formData.address} max={255} />
          </div>
        </div>
      </div>

      {/* ── Footer de acciones ── */}
      {canEdit && (
        <>
          <div className="border-t border-border" />
          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-lg sm:w-auto"
              onClick={handleCancel}
              disabled={!isDirty || saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Descartar cambios
            </Button>
            <Button
              type="submit"
              disabled={!isDirty || saving}
              className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </>
      )}
    </form>
  );
};

export default CompanyProfileTab;
